import { ReactNode, useState } from 'react';
import { Card } from '../../Atoms/Card';
import { CollapseButton } from '../../Atoms/CollapseButton';
import { useVisibleItemsCap } from './useVisibleItemsCap';

interface ActionNeededBoxProps {
  title: string;
  count: string;
  summary: string;
  testIdPrefix: string;
  contentKey: unknown;
  collapsible?: boolean;
  children: ReactNode;
}

export const ActionNeededBox = ({
  title,
  count,
  summary,
  testIdPrefix,
  contentKey,
  collapsible = false,
  children
}: ActionNeededBoxProps) => {
  const [collapsed, setCollapsed] = useState(false);
  const { listRef, contentRef, listMaxHeight } = useVisibleItemsCap(
    `[data-${testIdPrefix}-item]`,
    collapsed ? null : contentKey
  );

  return (
    <Card
      useDefaultCardStyles
      data-cy={`${testIdPrefix}-section`}
      className="my-6">
      <div className="flex items-center bg-card-1 h-[48px]">
        <div className="flex items-center gap-3 p-5">
          <span
            aria-hidden="true"
            className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[#D96E2B] text-[11px] font-bold leading-none text-white">
            !
          </span>
          <span className="text-[13px] font-semibold text-grey-9">{title}</span>
        </div>
        <span
          className="ml-auto self-center px-6 text-sm font-medium text-[#D96E2B]"
          data-testid={`${testIdPrefix}-count`}>
          {count}
        </span>
        {collapsible && (
          <CollapseButton
            classNames="mr-4"
            collapse={collapsed}
            setCollapse={setCollapsed}
            testId={`${testIdPrefix}-collapse`}
            label={title}
          />
        )}
      </div>
      {!collapsed && (
        <div
          className="border-t p-5 bg-white rounded-b-lg"
          data-testid={`${testIdPrefix}-section-body`}>
          <div className="text-sm text-grey-12 mb-4">{summary}</div>
          <div
            ref={listRef}
            className={`relative${listMaxHeight === null ? '' : ' overflow-y-auto pr-2'}`}
            style={listMaxHeight === null ? undefined : { maxHeight: listMaxHeight }}
            data-testid={`${testIdPrefix}-list`}>
            <div
              ref={contentRef}
              className="flex flex-col gap-4">
              {children}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};
